import { useEffect, useState } from "react";
import { loadClientEnv } from "../../config/env";

export type LoadingOverlayProps = {
  isPending: boolean;
  /**
   * 補助メッセージを追加表示するまでの遅延 (ms)。
   * テストで短い値を注入できるよう prop 化。省略時は env 値。
   */
  noticeDelayMs?: number;
};

/**
 * AI 生成中のフルスクリーンオーバーレイ。
 * Requirements 4.1 (生成中の状態表示) / 4.3 (長引きしたときの補助メッセージ)。
 */
export function LoadingOverlay({
  isPending,
  noticeDelayMs,
}: LoadingOverlayProps): JSX.Element | null {
  const [showNotice, setShowNotice] = useState(false);

  useEffect(() => {
    if (!isPending) {
      // pending が解除されたら次回の再表示に備えて初期化する
      setShowNotice(false);
      return;
    }

    // env の読み込みは pending=true のときだけ遅延評価する。テストや未 pending
    // では env を参照しないため、未設定の環境変数で落ちない。
    const delay = noticeDelayMs ?? loadClientEnv().longGenerationNoticeMs;

    setShowNotice(false);
    const timerId = window.setTimeout(() => {
      setShowNotice(true);
    }, delay);

    return () => {
      window.clearTimeout(timerId);
    };
  }, [isPending, noticeDelayMs]);

  if (!isPending) {
    return null;
  }

  return (
    <div
      role="status"
      aria-live="polite"
      data-testid="loading-overlay"
      className="fixed inset-0 z-50 flex flex-col items-center justify-center gap-3 bg-reader-overlay"
    >
      <div className="flex items-center gap-1.5 motion-reduce:hidden" aria-hidden="true">
        <span className="inline-block w-2.5 h-2.5 rounded-full bg-reader-primary animate-bounce [animation-delay:-0.3s]" />
        <span className="inline-block w-2.5 h-2.5 rounded-full bg-reader-primary animate-bounce [animation-delay:-0.15s]" />
        <span className="inline-block w-2.5 h-2.5 rounded-full bg-reader-primary animate-bounce" />
      </div>
      <p className="text-reader-primary text-lg">生成中です…</p>
      {showNotice ? (
        <p className="text-amber-300 text-sm">生成に時間がかかっています…</p>
      ) : null}
    </div>
  );
}
